import axios, { AxiosResponse } from "axios";
import { baseUrl } from "./ApiConfig";
import { pca, apiTokenRequest } from "./MsalConfig";
import { BusinessCard } from "./Storage";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Response {
  success: boolean;
  message: string;
  data?: any;
}

/* ------------------------------------------------------------------ */
/*  Axios instance                                                     */
/* ------------------------------------------------------------------ */

const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
   
  },
});

/**
 * Acquire a bearer token from MSAL and attach it to the request.
 *
 * This interceptor runs before every outgoing request, silently
 * acquiring an access token from the MSAL cache (or via a popup
 * if the cache is empty / expired).
 */
api.interceptors.request.use(async (config) => {
  try {
    const accounts = pca.getAllAccounts();

    if (accounts.length > 0) {
      const response = await pca.acquireTokenSilent({
        ...apiTokenRequest,
        account: accounts[0],
      });
      config.headers.Authorization = `Bearer ${response.accessToken}`;
    }
  } catch (error) {
    console.warn("Could not acquire token silently:", error);
  }
  return config;
});

// Response interceptor for debugging
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error Detailed Log:", {
      message: error.message,
      code: error.code,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        baseURL: error.config?.baseURL,
      },
      response: error.response?.data
    });
    return Promise.reject(error);
  }
);

/* ------------------------------------------------------------------ */
/*  Normalize API → BusinessCard                                       */
/* ------------------------------------------------------------------ */


/**
 * Map the backend's status strings to the UI's expected values.
 */
const mapStatus = (s: string): BusinessCard["status"] => {
  switch (s?.toLowerCase()) {
    case "active":
    case "approved":
      return "Approved";
    case "reject":
    case "rejected":
      return "Rejected";
    default:
      return "Pending";
  }
};

/**
 * Convert a raw API card object to the BusinessCard shape
 * used throughout the UI.
 */
export const normalizeCard = (c: any): BusinessCard => {
  console.log("Raw card data from backend:", c);
  return {
    id: c._id ?? c.id ?? "",
    userId: c.userId ?? "",
    userName: c.fullName ?? c.userName ?? "",
    name: c.fullName ?? c.name ?? "",
    role: c.jobRole ?? c.role ?? "",
    company: c.company ?? "",
    department: c.department ?? "",
    unit: c.unit ?? "",
    email: c.email ?? "",
    phone: c.phoneNumber ?? c.phone ?? "",
    personalTel: c.personalTel ?? "",
    website: c.website ?? "",
    photo: (c._id || c.id) ? getPassportImageUrl(c._id || c.id) : (c.photo || undefined),
    status: mapStatus(c.status),
    rejectionReason: c.rejectionReason,
    createdAt: c.createdAt ?? "",
  };
};

/** Normalize an array of cards. */
export const normalizeCards = (arr: any[]): BusinessCard[] =>
  (arr ?? []).map(normalizeCard);

/* ------------------------------------------------------------------ */
/*  API functions                                                      */
/* ------------------------------------------------------------------ */

/**
 * POST /signup — Create a new business card submission.
 */
export const createBusinessCardApi = (
  payload: any
): Promise<AxiosResponse<Response>> => {
  return api.post("/signup", payload);
};

/**
 * GET /users/:id — Get a business card by its MongoDB _id (authenticated).
 */
export const getUserCardApi = (
  id: string
): Promise<AxiosResponse<Response>> => {
  return api.get(`/users/${id}`);
};

/**
 * GET /user/:id — Public endpoint, no auth required.
 * Used when external users scan a QR code to view a card.
 */
export const getPublicCardApi = (
  id: string
): Promise<AxiosResponse<Response>> => {
  return axios.get(`${baseUrl}/user/${id}`);
};

/**
 * GET /users — Get all business cards (supervisor only).
 */
export const getAllCardsApi = (): Promise<AxiosResponse<Response>> => {
  return api.get("/users");
};

/**
 * PATCH /users/:id/status — Update card status.
 */
export const updateCardStatusApi = (
  id: string,
  status: "active" | "inactive" | "pending" | "reject"
): Promise<AxiosResponse<Response>> => {
  return api.patch(`/users/${id}/status`, { status });
};

/**
 * PATCH /users/:id — Update card fields (e.g. phone number).
 */
export const updateCardFieldApi = (
  id: string,
  fields: Record<string, string>
): Promise<AxiosResponse<Response>> => {
  return api.patch(`/users/${id}`, fields);
};

/**
 * POST /upload/:id — Upload passport photo (multipart/form-data).
 * @param id MongoDB card _id (or staffId) associated with the profile image.
 * @param file The image File object to upload.
 */
export const uploadPassportApi = (
  id: string,
  file: File
): Promise<AxiosResponse<Response>> => {
  console.log(`[uploadPassportApi] Uploading image for ID: ${id}, filename: ${file.name}, size: ${file.size} bytes`);
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/upload/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * Build the absolute URL for the passport image (GET /image/:id).
 * Use this directly as an <img src>.
 */
export const getPassportImageUrl = (key: string): string =>
  `${baseUrl}/image/${key}`;

/**
 * Fetch the passport image as a blob and return an Object URL.
 * Needed because the server returns image bytes with
 * Content-Type: application/json, which prevents normal <img src>.
 */
export const fetchPassportBlob = async (
  key: string
): Promise<string | null> => {
  try {
    // First, try a regular GET to see if it's a JSON response with a data field
    const res = await api.get(`/image/${key}`);
    console.log("[fetchPassportBlob] Raw response from /image:", res.data);

    if (res.data && res.data.success && res.data.imageBase64) {
      const rawData = res.data.imageBase64;
      console.log(`[fetchPassportBlob] Received imageBase64 response. Length: ${rawData?.length}`);
      return rawData; // This is a Data URL, we can return it directly
    }

    // Fallback: If it's not the wrapped JSON we expect, try fetching as a raw blob
    console.log("[fetchPassportBlob] Fallback to raw blob fetch...");
    const blobRes = await api.get(`/image/${key}`, { responseType: 'blob' });
    if (blobRes.data && blobRes.data.size > 100) {
      const imageBlob = new Blob([blobRes.data], { type: blobRes.headers['content-type'] || "image/png" });
      return URL.createObjectURL(imageBlob);
    }

    return null;
  } catch (error: any) {
    console.error("[fetchPassportBlob] Error fetching passport:", error.message);
    return null;
  }
};

